import React, { useCallback, useRef } from 'react';
import { identity } from 'ramda';
import uuid from 'uuid-random';
import { useTranslations } from 'js/hooks/i18n';
import { I18n } from 'js/components/common/I18n';
import { getClassNames } from 'js/utils/classNames.util';
import buildClassName from 'js/utils/classNameBuilder.util';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';

import * as constants from './constants';

import style from './CheckboxShallow.scss';

export const CheckboxShallow = ({
    id,
    type,
    label,
    hint,
    itemRef,
    children,
    disabled,
    readOnly,
    onChange,
    onKeyDown,
    className = '',
    classNames,
    indeterminate,
    kind = 'check',
    reverse = false,
    stretch = false,
    wrapText = false,
    isLabelAsteriskEnabled = false,
    ...props
}) => {
    const idRef = useRef(id || uuid());
    const checkboxRef = useRef();
    const ref = itemRef || checkboxRef;
    const translate = useTranslations();
    const handleOnKeyDown = useCallback((event) => {
        const { key } = event;

        onKeyDown?.();

        if (key === ' ' || key === 'Spacebar') {
            event.preventDefault();

            ref.current?.click();
        }
    }, [ref, onKeyDown]);
    const labelClassName = getClassNames([
        className,
        buildClassName(['checkbox'], style, classNames),
        reverse && style.reverse,
        stretch && style.stretch,
        (disabled || readOnly) && style.disabledCheckbox
    ]);

    const labelContent = typeof label === 'string'
        ? <I18n tKey={label} defaultValue={label} />
        : label;

    const hintClassName = buildClassName(['hint'], style, classNames);
    const hintContent = typeof hint === 'string'
        ? <I18n tKey={hint} defaultValue={hint} className={hintClassName} />
        : <span className={hintClassName}>{hint}</span>;

    return (
        <AccessibilityElement
            tagName="label"
            htmlFor={idRef.current}
            className={labelClassName}
            onKeyDown={handleOnKeyDown}
            tabIndex={disabled || readOnly ? -1 : 0}
            aria-label={props['aria-label'] || translate('playtech.mobile.system.checkbox.aria-label')}
        >
            <input
                tabIndex={-1}
                type="checkbox"
                ref={ref}
                id={idRef.current}
                disabled={disabled}
                readOnly={readOnly}
                onChange={readOnly ? identity : onChange}
                className={`${buildClassName(['input'], style, classNames)} ${readOnly ? buildClassName(['readOnly'], style, classNames) : ''} ${indeterminate ? buildClassName(['indeterminateIcon'], style, classNames) : ''}`}
                {...props}
            />
            <span className={buildClassName([constants.CHECKBOX_TYPES[kind]], style, classNames)} />
            {children}
            <span
                className={getClassNames([
                    buildClassName(['label'], style, classNames),
                    wrapText && style.wrapText,
                    (disabled || readOnly) && style.disabledLabel
                ])}
            >
                {labelContent}
                {isLabelAsteriskEnabled && <span className={style.fieldLabelAsterisk}>*</span>}
                {hint && hintContent}
            </span>
        </AccessibilityElement>
    );
};
